import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { BrandMark } from "@/components/BrandMark";
import { useToast } from "@/components/Toaster";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { WhatUsesThis } from "../automations/WhatUsesThis";
import { LoadError } from "@/components/LoadError";
import { backToOriginal, switchHelp, switchLabel, valueLabel } from "./siteSwitches";
import { SiteSwitchSetButton } from "./SiteSwitchSetDialog";

interface Site {
  site: string;
  name: string;
  brand_id?: number | null;
  base_url: string;
  access_keys: string[];
  flags: Record<string, string>;
  notes?: string;
}

interface Switch {
  name: string;
  values: string[];
  default: string;
  sites: string[];
  description: string;
}


/** The switches that move each website from Keap to the CRM (crm-sites). Every flip asks first. */
export function SitesPanel() {
  const q = useQuery({ queryKey: ["sites"], queryFn: () => api<{ sites: Site[]; switches: Switch[] }>("/api/v1/sites") });
  const [flip, setFlip] = useState<{ site: Site; sw: Switch; to: string } | null>(null);
  const [usesKey, setUsesKey] = useState<string | null>(null);
  if (q.isLoading) return <Skeleton className="h-64" />;
  if (q.error) return <LoadError className="px-0" what="the site switches" error={q.error} onRetry={() => q.refetch()} />;
  const sites = q.data?.sites ?? [];
  const switches = q.data?.switches ?? [];
  // Two boards: the sign-in sites (Club, Manager Training, courses) and the university sites + admin.
  const signIn = new Set(["club", "mhcmt", "courses"]);
  const groups = [
    { title: "Sign-in sites", sub: "Club, Manager Training and courses: where passwords, membership and rosters come from", test: (sw: Switch) => sw.sites.some((x) => signIn.has(x)) },
    { title: "University sites and admin", sub: "Where forms and buy links go", test: (sw: Switch) => !sw.sites.some((x) => signIn.has(x)) },
  ];
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <SiteSwitchSetButton />
      </div>
      {groups.map((g) => {
        const sws = switches.filter(g.test);
        if (sws.length === 0) return null;
        const cols = sites.filter((st) => sws.some((sw) => sw.sites.includes(st.site)));
        return (
          <Card key={g.title} className="overflow-x-auto">
            <CardHeader>
              <CardTitle>{g.title}</CardTitle>
              <span className="text-xs text-[var(--color-muted-foreground)]">{g.sub}</span>
            </CardHeader>
            <table className="w-full border-collapse text-sm">
              <thead className="border-b bg-[var(--color-muted)] text-left text-xs text-[var(--color-muted-foreground)]">
                <tr>
                  <th className="sticky left-0 min-w-[18rem] bg-[var(--color-muted)] px-3 py-2 font-medium">Switch</th>
                  {cols.map((st) => (
                    <th key={st.site} className="whitespace-nowrap px-2 py-2 font-medium">
                      <div>{st.name}</div>
                      <div className="flex items-center gap-1 font-normal">
                        {st.brand_id ? <BrandMark id={st.brand_id} /> : null}
                        <span className="num">{st.site}</span>
                      </div>
                      {(st.access_keys ?? []).map((k) => (
                        <button key={k} type="button" className="block text-xs font-normal text-[var(--color-primary)] hover:underline" onClick={() => setUsesKey(k)}>
                          {k} access: what uses this?
                        </button>
                      ))}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sws.map((sw) => (
                  <tr key={sw.name} className="border-b align-top last:border-0">
                    <th scope="row" className="sticky left-0 min-w-[18rem] max-w-[26rem] bg-[var(--color-card)] px-3 py-2 text-left font-normal">
                      <div className="font-medium">{switchLabel[sw.name] ?? sw.name}</div>
                      <div className="text-xs text-[var(--color-muted-foreground)]" title={switchHelp[sw.name] ? sw.description : undefined}>
                        {switchHelp[sw.name] ?? sw.description}
                      </div>
                    </th>
                    {cols.map((st) => {
                      const cur = st.flags[sw.name] ?? sw.default;
                      if (!sw.sites.includes(st.site)) return <td key={st.site} className="px-2 py-2 text-center text-xs text-[var(--color-muted-foreground)]">—</td>;
                      return (
                        <td key={st.site} className="px-2 py-2">
                          <Select
                            className={cn("w-36", cur !== sw.default && "[&_select]:border-[var(--color-warning)] [&_select]:font-semibold")}
                            value={cur}
                            onChange={(e) => e.target.value !== cur && setFlip({ site: st, sw, to: e.target.value })}
                            aria-label={`${switchLabel[sw.name] ?? sw.name} on ${st.name}`}
                          >
                            {sw.values.map((v) => (
                              <option key={v} value={v}>
                                {valueLabel(sw.name, v)}
                              </option>
                            ))}
                          </Select>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        );
      })}
      <p className="text-xs text-[var(--color-muted-foreground)]">
        Each box shows what the site uses now. Every switch started at how the site worked before the CRM (mostly Keap); one in bold has been moved off that. A flip
        reaches the site within about a minute and asks first. Hover a switch's description for the details.
      </p>
      {flip && <FlipDialog flip={flip} onClose={() => setFlip(null)} />}
      {usesKey && <WhatUsesThis kind="access" idOrKey={usesKey} onClose={() => setUsesKey(null)} />}
    </div>
  );
}

function FlipDialog({ flip, onClose }: { flip: { site: Site; sw: Switch; to: string }; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const from = flip.site.flags[flip.sw.name] ?? flip.sw.default;
  const set = useMutation({
    mutationFn: () =>
      api<{ changed: string[] }>(`/api/v1/sites/${flip.site.site}/flags`, { json: { flags: { [flip.sw.name]: flip.to === flip.sw.default ? "default" : flip.to } } }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["sites"] });
      toast(`${flip.site.name}: ${r.changed.join(", ") || "no change"}`);
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  const backToToday = flip.to === flip.sw.default;
  const name = switchLabel[flip.sw.name] ?? flip.sw.name;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>
          {name} on {flip.site.name}: {valueLabel(flip.sw.name, from)} → {valueLabel(flip.sw.name, flip.to)}?
        </DialogTitle>
        <DialogDescription>{switchHelp[flip.sw.name] ?? flip.sw.description}</DialogDescription>
        <div className="space-y-2 text-sm">
          {switchHelp[flip.sw.name] && (
            <details className="text-xs text-[var(--color-muted-foreground)]">
              <summary className="cursor-pointer">The full rule</summary>
              <p className="mt-1">{flip.sw.description}</p>
            </details>
          )}
          <p>
            {backToToday ? (
              <>{backToOriginal(flip.sw.name, flip.sw.default)}</>
            ) : (
              <>
                Everyone using <span className="font-medium">{flip.site.base_url || flip.site.name}</span> gets the new behavior within about a minute. You can switch it back
                here the same way.
              </>
            )}
          </p>
          {!backToToday && (
            <p className="flex items-start gap-2 rounded-md bg-[var(--color-warning-bg)] px-3 py-2 text-xs text-[var(--color-warning-fg)]">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0" /> This changes a live website. The site's code for this switch must already be live there, or the switch does
              nothing.
            </p>
          )}
          <div className="flex flex-wrap gap-1 text-xs">
            <span className="text-[var(--color-muted-foreground)]">Choices:</span>
            {flip.sw.values.map((v) => (
              <Badge key={v} variant={v === flip.to ? "default" : "muted"}>
                {valueLabel(flip.sw.name, v)}
                {v === from ? " (now)" : ""}
              </Badge>
            ))}
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Leave it
          </Button>
          <Button variant={backToToday ? "default" : "destructive"} disabled={set.isPending} onClick={() => set.mutate()}>
            Switch to {valueLabel(flip.sw.name, flip.to)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
